import { sortBy } from 'lodash';
import React, { useEffect } from 'react';
import { NotebookContext } from '../../../context/NotebookContext';
import client from '../../../lib/client';
import { createOrUpdateSubject, subjectBySlugQuery } from '../../../queries/subjects';
import {
  CreateOrUpdateSubject,
  CreateOrUpdateSubjectVariables
} from '../../../queries/__generated__/CreateOrUpdateSubject';
import ErrorMessages from '../../ErrorMessages';
import { Switch } from '../../forms';
import NewGroupModal from '../../NewGroupModal';
import { Card } from '../../ui';
import { ErrorMessagesArray } from '../tracker/types';
import { Subject } from './types';

interface SubjectGroupProps {
  subject: Subject;
}

const SubjectGroups: React.FC<SubjectGroupProps> = ({ subject }) => {
  const { notebook, reloadNotebook } = React.useContext(NotebookContext);
  const [groupIds, setGroupIds] = React.useState(subject.groups.map(g => g.id));
  const [errors, setErrors] = React.useState<ErrorMessagesArray>([]);
  const notebookGroups = sortBy(notebook.groups, 'name') || [];
  const [showNewModal, setShowNewModal] = React.useState(false);

  useEffect(() => {
    setGroupIds(subject.groups.map(s => s.id));
  }, [subject]);

  async function saveSubject(updatedGroupIds: string[]) {
    const res = await client.mutate<CreateOrUpdateSubject, CreateOrUpdateSubjectVariables>({
      mutation: createOrUpdateSubject,
      variables: {
        input: {
          notebookId: notebook.id,
          name: subject.name,
          id: subject.id,
          groupIds: updatedGroupIds
        }
      },
      refetchQueries: [
        {
          query: subjectBySlugQuery,
          variables: { slug: subject.slug, notebookId: notebook.id }
        }
      ]
    });

    if (res.data!.createOrUpdateSubject.errors) {
      setErrors(res.data!.createOrUpdateSubject.errors);
      return;
    }

    if (errors.length) {
      setErrors([]);
    }

    reloadNotebook();
  }

  function toggleGroup(groupId: string) {
    if (groupIds.includes(groupId)) {
      const updatedGroups = groupIds.filter((id: string) => id !== groupId);
      saveSubject(updatedGroups);
    } else {
      saveSubject(groupIds.concat(groupId));
    }
  }

  return (
    <Card className="pa4 mb2">
      <div className="flex justify-between">
        <h2 className="f5 fw6 mv0">Include in:</h2>
        <button className="f7 blue hover-navy pointer" onClick={() => setShowNewModal(true)}>
          New Group
        </button>
      </div>

      <ErrorMessages errors={errors} />

      {notebookGroups.length > 0 && (
        <ul className="list pa0" data-testid="groups-list">
          {sortBy(notebookGroups).map(group => (
            <li
              key={group.id}
              data-testid={`group-${group.slug}`}
              className="pv2 bt b--light-gray flex items-center justify-between"
            >
              <span className="f6">{group.name}</span>
              <Switch active={groupIds.includes(group.id)} onClick={() => toggleGroup(group.id)} />
            </li>
          ))}
        </ul>
      )}

      {notebook.groups.length === 0 && (
        <ul className="list pa0" data-testid="groups-list-empty">
          <li className="i moon-gray flex justify-between items-center f6 serif lh-copy bt b--light-gray pv2">
            You don&rsquo;t have any groups yet.
          </li>
        </ul>
      )}

      {showNewModal && <NewGroupModal closeModal={() => setShowNewModal(false)} />}
    </Card>
  );
};

export default SubjectGroups;
